import * as Styled from './PhotosTab.styles'

import {
  Box,
  CircularProgress,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material'
import { UploadEventPhotoModal, ViewPhotoModal } from '../../../../modals'

import AddPhotoButton from '../../../../components/AddPhotoButton/AddPhotoButton'
import ImageList from '@mui/material/ImageList'
import ImageListItem from '@mui/material/ImageListItem'
import RefreshIcon from '@mui/icons-material/Refresh'
import { S3Image } from '../../../../types/s3image'
import { Stack } from '@mui/system'
import { refreshLiveEventPhotos } from '../../../../store/actions/liveEventActions'
import { useAppDispatch } from '../../../../store/hooks'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useSelector } from 'react-redux'
import { useCallback, useEffect, useState } from 'react'
import { useTheme } from '@mui/material'
import {
  HOURS_24_IN_MILLISECONDS,
  SECONDS_TO_MILLISECONDS,
} from '../../../../constants/time'
import { ReactionSummary } from '../../../../components'
import { GET_PHOTO_REACTIONS } from '../../../../services/endpoints'
import axiosAPI from '../../../../services/axios'

const PhotosTab = () => {
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('desktop'))
  const columns = isLargeScreen ? 4 : 1

  const eventLoading = useSelector((state: any) => state.liveEvent.loading)

  const event = useSelector((state: any) => state.liveEvent.data)

  const eventImages = useSelector((state: any) => state.liveEvent.photos)

  const userEmail = useSelector((state: any) => state.user.email)

  const [selectedImage, setSelectedImage] = useState<S3Image | null>(null)
  const [imageSrcArray, setImageSrcArray] = useState<string[]>([])
  const [uploadEventPhotoFileArray, setUploadEventPhotoFileArray] = useState<
    File[]
  >([])
  const [uploadCaptionsArray, setUploadCaptionsArray] = useState<string[]>([])

  const [openDialog, setOpenDialog] = useState(false)
  const [openPhotoUploadDialog, setOpenPhotoUploadDialog] = useState(false)

  const [photoReactions, setPhotoReactions] = useState<{
    [key: string]: { [reaction: string]: number }
  }>({})

  const [longPressedPhotoKey, setLongPressedPhotoKey] = useState<string | null>(
    null,
  )

  const eventEndTime = event?.endDateTime
    ? new Date(event.endDateTime * SECONDS_TO_MILLISECONDS)
    : null
  const twentyFourHoursAfterEnd = eventEndTime
    ? new Date(eventEndTime.getTime() + HOURS_24_IN_MILLISECONDS)
    : null
  const isPhotoUploadDisabled =
    twentyFourHoursAfterEnd && new Date() > twentyFourHoursAfterEnd

  /**
   * Fetches the reaction data for a single photo from the backend.
   * Converts the backend response into a simple { reactionType: count } shape.
   * This ensures the frontend shows the total number of users for each reaction type.
   */

  const fetchPhotoReactions = useCallback(
    async (eventId: string, imageKey: string) => {
      const url = GET_PHOTO_REACTIONS(eventId, imageKey)
      const response = await axiosAPI.get(url)
      const rawReactions = response.data.result || {}

      const reactionCounts: { [reaction: string]: number } = {}
      for (const [reactionName, reactionData] of Object.entries(rawReactions)) {
        const users = (reactionData as any).users || []
        reactionCounts[reactionName] = users.length
      }

      return reactionCounts
    },
    [],
  )

  const handleImageClick = (item: S3Image) => {
    setSelectedImage(item)
    setOpenDialog(true)
  }

  const handleCaptionChange = (photoIndex: number, caption: string) => {
    const newUploadCaptionsArray = [...uploadCaptionsArray]
    newUploadCaptionsArray[photoIndex] = caption
    setUploadCaptionsArray(newUploadCaptionsArray)
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault()
    const files = event.target.files

    if (files) {
      const newFilesArray = Array.from(files)
      setUploadEventPhotoFileArray((prevFiles) => [
        ...prevFiles,
        ...newFilesArray,
      ])

      const newImageSrcArray = newFilesArray.map((file) => {
        return new Promise<string | ArrayBuffer>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = (e: ProgressEvent<FileReader>) => {
            if (e.target && typeof e.target.result === 'string') {
              resolve(e.target.result)
            } else {
              reject('Error reading file')
            }
          }
          reader.readAsDataURL(file)
        })
      })

      Promise.all(newImageSrcArray).then((srcArray) => {
        setImageSrcArray((prevSrcArray) => [
          ...prevSrcArray,
          ...(srcArray as string[]),
        ])
      })

      setOpenPhotoUploadDialog(true)
    }
  }

  /**
   * On mount (or when images or eventId change), fetch all reactions
   * for every photo and store them in state.
   * This ensures that when the page first loads, each photo shows its correct reaction summary.
   */

  useEffect(() => {
    const fetchAllReactions = async () => {
      if (!eventImages?.data?.length || !event?.eventId) return

      for (const photo of eventImages.data) {
        const backendReactions = await fetchPhotoReactions(
          event.eventId,
          photo.key,
        )
        setPhotoReactions((prev) => ({
          ...prev,
          [photo.key]: backendReactions,
        }))
      }
    }

    fetchAllReactions()
  }, [eventImages?.data, event?.eventId, fetchPhotoReactions])

  /**
   * Continuously refresh photo reactions every 5 seconds
   * to keep reaction counts updated in real time (e.g., during live events).
   * This ensures users see up-to-date feedback from other attendees.
   */

  useEffect(() => {
    const interval = setInterval(() => {
      if (!eventImages?.data?.length || !event?.eventId) return

      eventImages.data.forEach(async (photo: S3Image) => {
        const backendReactions = await fetchPhotoReactions(
          event.eventId,
          photo.key,
        )
        setPhotoReactions((prev) => ({
          ...prev,
          [photo.key]: backendReactions,
        }))
      })
    }, 5000)

    return () => clearInterval(interval)
  }, [eventImages?.data, event?.eventId, fetchPhotoReactions])

  const closeModal = () => {
    setOpenPhotoUploadDialog(false)
    setImageSrcArray([])
    setUploadEventPhotoFileArray([])
    setUploadCaptionsArray([])
  }

  // console.log('Posting:', selectedImage)

  return (
    <Styled.PaddedContainer>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{
          marginBottom: theme.spacing(3),
        }}
      >
        <Box display="flex" alignItems="center">
          <Box marginRight="8px">
            {eventImages.refreshing ? (
              <Box margin="8px">
                <CircularProgress size={'24px'} />
              </Box>
            ) : (
              <IconButton
                onClick={() => {
                  dispatch(refreshLiveEventPhotos(event.eventId))
                }}
              >
                <RefreshIcon />
              </IconButton>
            )}
          </Box>
          {!eventLoading && event?.eventId !== '' ? (
            <Typography variant="h3">{`Photos (${eventImages.data.length})`}</Typography>
          ) : (
            <Skeleton variant="text" sx={{ width: '100px', height: '24px' }} />
          )}
        </Box>

        <AddPhotoButton
          variant="none"
          disabled={
            eventImages.loading || eventLoading || isPhotoUploadDisabled
          }
          handleFileChange={handleFileChange}
        />
        {!eventLoading && event?.eventId !== '' && (
          <UploadEventPhotoModal
            handleCaptionChange={handleCaptionChange}
            uploadCaptionsArray={uploadCaptionsArray}
            selectedImgArray={imageSrcArray}
            open={openPhotoUploadDialog}
            onClose={closeModal}
            eventId={event?.eventId}
            uploadEventPhotoFileArray={uploadEventPhotoFileArray}
            handleFileChange={handleFileChange}
            deletePhotos={(index?: number) => {
              if (index !== undefined) {
                const updatedSelectedImages = [...imageSrcArray]
                const updatedUploadEventPhotos = [...uploadEventPhotoFileArray]
                updatedSelectedImages.splice(index, 1)
                updatedUploadEventPhotos.splice(index, 1)
                setImageSrcArray(updatedSelectedImages)
                setUploadEventPhotoFileArray(updatedUploadEventPhotos)
              } else {
                setImageSrcArray([])
                setUploadEventPhotoFileArray([])
              }
            }}
          />
        )}
      </Stack>
      {!eventImages.success ? (
        <ImageList gap={24} cols={columns}>
          {Array.from(new Array(12)).map((_, index) => (
            <ImageListItem key={index}>
              <Skeleton
                variant="rectangular"
                style={{ borderRadius: '8px', height: '200px' }}
              />
            </ImageListItem>
          ))}
        </ImageList>
      ) : (
        <ImageList gap={24} cols={columns}>
          {eventImages?.data.map((photo: S3Image) => {
            const reactions = photoReactions[photo.key] || {}

            return (
              <ImageListItem
                key={photo.key}
                onClick={() => handleImageClick(photo)}
                onTouchStart={(e) => {
                  if (isLargeScreen) return

                  const timeoutId = setTimeout(() => {
                    setLongPressedPhotoKey(photo.s3Url)
                  }, 600)

                  e.currentTarget.ontouchend = () => {
                    clearTimeout(timeoutId)
                    setTimeout(() => {
                      setLongPressedPhotoKey(null)
                    }, 1500)
                  }
                }}
              >
                <Styled.GridImageWrapper>
                  <Styled.GridImage
                    srcSet={photo.s3Url}
                    src={photo.s3Url}
                    alt={photo.name}
                    loading="lazy"
                  />
                  {reactions && Object.keys(reactions).length > 0 && (
                    <Styled.Overlay
                      className="overlay"
                      style={{
                        opacity:
                          longPressedPhotoKey === photo.s3Url ? 1 : undefined,
                        // pointerEvents: isLargeScreen ? 'auto' : undefined,
                      }}
                    >
                      <ReactionSummary
                        reactions={reactions}
                        variant="grid-view"
                      />
                    </Styled.Overlay>
                  )}
                </Styled.GridImageWrapper>
              </ImageListItem>
            )
          })}
        </ImageList>
      )}
      {event && selectedImage && (
        <ViewPhotoModal
          caption={selectedImage?.caption || ''}
          selectedImg={selectedImage}
          currentTime={selectedImage?.lastModified?.toString() || ''}
          uploaderName={selectedImage?.uploaderName || ''}
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          eventId={event.eventId || ''}
          userEmail={userEmail || ''}
          photoId={selectedImage?.key || ''}
        />
      )}
    </Styled.PaddedContainer>
  )
}

export default PhotosTab
